import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { Box, Button, IconButton, Typography as MuiTypography } from '@mui/material';
import type { FC, MutableRefObject, Ref } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import type { Path } from 'react-hook-form';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import { MuiFormInput, MuiFormSelect } from 'components/Form';
import type { MuiFormSelectOptionT } from 'components/Form/MuiFormSelect/MuiFormSelect';
import { useResponsive } from 'contexts/ResponsiveContext';
import type { SelectOptionT } from 'declarations/types/select';
import {
  mergeFilesToDataTransferFileList,
  removeFileAtIndexFromFileList,
} from 'helpers/mergeFilesToDataTransferFileList';
import { CloseIcon } from 'icons';

import type { NewTicketFormValuesT } from '../../declarations';
import { clearCreationAttachmentFormFields } from '../../helpers/clearCreationAttachmentFormFields';

import { DropZoneOuterStyled } from './NewTicketCreationAttachmentsSection.styled';

function assignDropzoneInputAndFieldRef(
  el: HTMLInputElement | null,
  dropzoneInputRef: MutableRefObject<HTMLInputElement | null>,
  fieldRef: Ref<HTMLInputElement> | undefined
): void {
  dropzoneInputRef.current = el;
  if (fieldRef == null) {
    return;
  }
  if (typeof fieldRef === 'function') {
    fieldRef(el);
  } else {
    (fieldRef as MutableRefObject<HTMLInputElement | null>).current = el;
  }
}

export type NewTicketCreationAttachmentsSectionPropsT = {
  /** When null, uses single-ticket field names (`creationAttachmentFile`, …). */
  rowIdSuffix: string | null;
  allowsAttachments: boolean;
  creationAttachmentTypesOptions: SelectOptionT[];
  isLoadingCreationAttachmentTypesOptions: boolean;
};

function fieldNames(rowIdSuffix: string | null): {
  note: Path<NewTicketFormValuesT>;
  typeCode: Path<NewTicketFormValuesT>;
  file: Path<NewTicketFormValuesT>;
  photo: Path<NewTicketFormValuesT>;
} {
  if (rowIdSuffix == null) {
    return {
      note: 'creationAttachmentNote',
      typeCode: 'creationAttachmentTypeCode',
      file: 'creationAttachmentFile',
      photo: 'creationAttachmentPhoto',
    };
  }
  return {
    note: `creationAttachmentNote-${rowIdSuffix}` as Path<NewTicketFormValuesT>,
    typeCode: `creationAttachmentTypeCode-${rowIdSuffix}` as Path<NewTicketFormValuesT>,
    file: `creationAttachmentFile-${rowIdSuffix}` as Path<NewTicketFormValuesT>,
    photo: `creationAttachmentPhoto-${rowIdSuffix}` as Path<NewTicketFormValuesT>,
  };
}

const NewTicketCreationAttachmentsSection: FC<NewTicketCreationAttachmentsSectionPropsT> = ({
  rowIdSuffix,
  allowsAttachments,
  creationAttachmentTypesOptions,
  isLoadingCreationAttachmentTypesOptions,
}) => {
  const { isMobile } = useResponsive();
  const { setValue, getValues, control, formState } = useFormContext<NewTicketFormValuesT>();
  const names = useMemo(() => fieldNames(rowIdSuffix), [rowIdSuffix]);

  const { field: fileField } = useController({
    control,
    name: names.file,
    defaultValue: undefined,
  });
  const { field: photoField } = useController({
    control,
    name: names.photo,
    defaultValue: undefined,
  });

  const watchedFile = useWatch({ control, name: names.file });
  const watchedPhoto = useWatch({ control, name: names.photo });

  useEffect(() => {
    if (!allowsAttachments) {
      clearCreationAttachmentFormFields(setValue, rowIdSuffix, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [allowsAttachments, rowIdSuffix, setValue]);

  const mergeIntoFileField = useCallback(
    (incoming: File[]): void => {
      if (incoming.length === 0) {
        return;
      }
      const existing = getValues(names.file) as FileList | undefined;
      const next = mergeFilesToDataTransferFileList(existing, incoming);
      fileField.onChange(next);
      photoField.onChange(undefined);
    },
    [fileField, getValues, names.file, photoField]
  );

  const mergeIntoPhotoField = useCallback(
    (incoming: File[]): void => {
      if (incoming.length === 0) {
        return;
      }
      const existing = getValues(names.photo) as FileList | undefined;
      const next = mergeFilesToDataTransferFileList(existing, incoming);
      photoField.onChange(next);
      fileField.onChange(undefined);
    },
    [fileField, getValues, names.photo, photoField]
  );

  const {
    getRootProps,
    getInputProps,
    inputRef: fileDropzoneInputRef,
    isDragActive,
    open: openFilePicker,
  } = useDropzone({
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: !allowsAttachments,
    onDropAccepted: mergeIntoFileField,
  });

  const {
    getInputProps: getPhotoInputProps,
    inputRef: photoDropzoneInputRef,
    open: openPhotoPicker,
  } = useDropzone({
    multiple: true,
    accept: { 'image/*': [] },
    noClick: true,
    noKeyboard: true,
    disabled: !allowsAttachments,
    onDropAccepted: mergeIntoPhotoField,
  });

  const mergedFileInputRef = useCallback(
    (el: HTMLInputElement | null): void => {
      assignDropzoneInputAndFieldRef(el, fileDropzoneInputRef, fileField.ref);
    },
    [fileDropzoneInputRef, fileField.ref]
  );

  const mergedPhotoInputRef = useCallback(
    (el: HTMLInputElement | null): void => {
      assignDropzoneInputAndFieldRef(el, photoDropzoneInputRef, photoField.ref);
    },
    [photoDropzoneInputRef, photoField.ref]
  );

  const removeAttachmentAtIndex = useCallback(
    (index: number): void => {
      const files = getValues(names.file) as FileList | undefined;
      if (files?.length) {
        const next = removeFileAtIndexFromFileList(files, index);
        fileField.onChange(next);
        return;
      }
      const photos = getValues(names.photo) as FileList | undefined;
      if (photos?.length) {
        const next = removeFileAtIndexFromFileList(photos, index);
        photoField.onChange(next);
      }
    },
    [fileField, getValues, names.file, names.photo, photoField]
  );

  const attachmentRows = useMemo((): { file: File; index: number }[] => {
    let list: FileList | undefined;
    if (watchedFile && (watchedFile as FileList).length > 0) {
      list = watchedFile as FileList;
    } else if (watchedPhoto && (watchedPhoto as FileList).length > 0) {
      list = watchedPhoto as FileList;
    }
    if (list == null) {
      return [];
    }
    return Array.from(list).map((file, index) => ({ file, index }));
  }, [watchedFile, watchedPhoto]);

  const typeOptions: MuiFormSelectOptionT[] = useMemo(
    () =>
      creationAttachmentTypesOptions.map(o => ({
        value: o.value,
        label: o.label,
      })),
    [creationAttachmentTypesOptions]
  );

  const errMap = formState.errors as Record<string, { message?: string } | undefined>;
  const fileError = errMap[names.file]?.message;
  const photoError = errMap[names.photo]?.message;
  const typeError = errMap[names.typeCode]?.message;

  if (!allowsAttachments) {
    return (
      <MuiTypography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Attachments are not available for this request category.
      </MuiTypography>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <MuiTypography variant="subtitle2" fontWeight={700}>
        Attachments (optional)
      </MuiTypography>
      <Box sx={{ mb: 1, mt: 0.5 }}>
        <MuiFormInput name={names.note} label="Attachment note" placeholder="Attachment note" />
      </Box>
      <Box
        sx={{ mb: 1 }}
        data-testid={rowIdSuffix == null ? 'new-ticket-attachment-type-select' : undefined}
      >
        <MuiFormSelect
          name={names.typeCode}
          label="Attachment type"
          placeholder=""
          options={typeOptions}
          isLoading={isLoadingCreationAttachmentTypesOptions}
        />
      </Box>

      <DropZoneOuterStyled
        {...getRootProps()}
        data-drag-active={isDragActive ? 'true' : 'false'}
        sx={{ cursor: allowsAttachments ? 'copy' : 'default' }}
      >
        <input
          {...getInputProps({
            'aria-label': 'Drop files to attach',
            name: fileField.name,
            onBlur: fileField.onBlur,
            ref: mergedFileInputRef,
          })}
          data-testid={rowIdSuffix == null ? 'new-ticket-attachment-file-input' : undefined}
        />
        {isMobile && (
          <input
            {...getPhotoInputProps({
              name: photoField.name,
              onBlur: photoField.onBlur,
              ref: mergedPhotoInputRef,
              capture: 'user',
            })}
            data-testid={rowIdSuffix == null ? 'new-ticket-attachment-photo-input' : undefined}
          />
        )}

        <MuiTypography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Drag and drop files here, or use the buttons below.
        </MuiTypography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon />}
            onClick={(): void => {
              openFilePicker();
            }}
            disabled={!allowsAttachments}
          >
            Choose files
          </Button>
          {isMobile && (
            <Button
              type="button"
              variant="outlined"
              size="small"
              startIcon={<PhotoCameraIcon />}
              onClick={(): void => {
                openPhotoPicker();
              }}
              disabled={!allowsAttachments}
            >
              Photos
            </Button>
          )}
        </Box>

        <Box sx={{ mt: 1, textAlign: 'left' }}>
          {attachmentRows.length === 0 ? (
            <MuiTypography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              No files selected
            </MuiTypography>
          ) : (
            attachmentRows.map(({ file, index }) => (
              <Box
                key={`${file.name}-${index}-${file.size}-${file.lastModified}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  py: 0.25,
                  pr: 0.5,
                }}
              >
                <MuiTypography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flex: 1, minWidth: 0 }}
                  noWrap
                >
                  {file.name}
                </MuiTypography>
                <IconButton
                  type="button"
                  size="small"
                  aria-label={`Remove ${file.name}`}
                  onClick={(e): void => {
                    e.stopPropagation();
                    removeAttachmentAtIndex(index);
                  }}
                  sx={{ flexShrink: 0, p: 0.25 }}
                >
                  <CloseIcon size="sm" />
                </IconButton>
              </Box>
            ))
          )}
        </Box>
      </DropZoneOuterStyled>

      {(fileError || photoError || typeError) && (
        <MuiTypography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {String(fileError || photoError || typeError || '')}
        </MuiTypography>
      )}
    </Box>
  );
};

export default NewTicketCreationAttachmentsSection;
